/**
 * @file Homepage About.
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import debounce from 'just-debounce-it';

/**
 * Homepage About component.
 */
const vHpAbout = () => {
	const hpAbout = document.querySelector('.v-hp-about');

	if (!hpAbout) {
		return;
	}

	gsap.registerPlugin(ScrollTrigger);
	const mm = gsap.matchMedia();
	const ball = hpAbout.querySelector('.v-hp-about__ball');
	const graphic = hpAbout.querySelector('.v-hp-about__graphic');
	const title = hpAbout.querySelector('.v-hp-about__title');
	// const words = title.querySelectorAll('.v-hp-about__word')
	const firstWord = title.querySelector('.v-hp-about__word');
	const dot = title.querySelector('.v-hp-about__title-end');

	/**
	 * Get the first line of words in the title.
	 *
	 * @returns {Array} Array of words that are on the first line of the title.
	 */
	const getFirstLine = () => {
		return [...title.querySelectorAll('.v-hp-about__word')].filter((word) => {
			const titleRect = title.getBoundingClientRect();
			const wordRect = word.getBoundingClientRect();
			const wordTop = wordRect.top + window.scrollY;
			const wordBottom = wordRect.bottom + window.scrollY;
			const titleTop = titleRect.top + window.scrollY;
			const firstWordBottom = firstWord.getBoundingClientRect().bottom + window.scrollY;

			return wordTop >= titleTop &&
				wordRect.left >= titleRect.left + window.scrollX &&
				wordBottom <= firstWordBottom; // firefox calculates box slightly differently
		});
	};

	// More vars
	let firstLine = getFirstLine();
	let bounceTl = null;
	let isPlaying = false;

	// GSAP matchMedia
	mm.add({
		isDesktop:  '(min-width: 821px)',
		isMobile:   '(max-width: 820px)', // if needed later
	}, (ctx) => {
		const {isDesktop} = ctx.conditions;

		if (!isDesktop) {
			return;
		}

		gsap.from(graphic, {
			y: '12%',
			opacity: 0,
			scrollTrigger: {
				trigger: hpAbout,
				start: 'top center',
				end: 'bottom bottom',
				scrub: true,
			}
		});
	});

	/**
	 * Set the initial position of the ball
	 * relative to the first word in the title.
	 */
	const setInitialPosition = () => {
		ball.style.setProperty('--ball-left', `${firstWord.getBoundingClientRect().left + (firstWord.offsetWidth / 2)}px`);
	};

	/**
	 * Build the bounce timeline.
	 */
	const buildBounceTl = () => {
		setInitialPosition();

		bounceTl = gsap.timeline({
			paused: true,
			onStart: () => {
				isPlaying = true;
				ball.classList.remove('hidden');
			},
			onComplete: () => {
				dot.classList.add('visible');
				ball.classList.add('hidden');
				gsap.set(ball, {clearProps: 'all'});
				setInitialPosition();
				isPlaying = false;
			},
			scrollTrigger: {
				trigger: hpAbout,
				start: () => `top top+=${parseFloat(getComputedStyle(document.querySelector('.v-sticky-header')).getPropertyValue('--header-height'), 10)}px`,
				onEnter: () => {
					bounceTl.timeScale(1.8);
				},
				onLeaveBack: () => {
					dot.classList.remove('visible');
					ball.classList.add('hidden');
				},
				toggleActions: 'play pause resume reset',
				invalidateOnRefresh: true,
			},
		});

		// Set up vars
		const sectionRect = hpAbout.getBoundingClientRect();
		const dotRect = dot.getBoundingClientRect();

		firstLine.forEach((word, i) => {
			const ballRect = ball.getBoundingClientRect();
			const getYFromRect = (rect) => (rect.top + window.scrollY) - (sectionRect.top + window.scrollY);
			const getXFromRect = (rect) => (rect.left + window.scrollX ) + (rect.width / 2) - (ballRect.left + (ballRect.width / 2));
			const wordRect = word.getBoundingClientRect();
			const prevWord = i > 0 ? firstLine[i - 1] : null;
			const isLastWord = i === firstLine.length - 1;

			// Start animation
			bounceTl.addLabel(`word-${i}-start`);

			// animate color change
			if (word !== firstWord) {
				bounceTl.to(ball, {
					backgroundColor: word.dataset.fill,
					duration: 0.2,
					ease: 'power1.inOut',
					transformOrigin: 'center bottom',
				}, `word-${i}-start`);
			}

			// animate y position
			if (word === firstWord) {
				bounceTl
				.to(ball, {
					y: () => getYFromRect(wordRect),
					duration: .4,
					ease: 'power1.inOut',
					transformOrigin: 'center bottom',
				}, `word-${i}-start`);
			} else {
				const bounces = [0, 40, 20];
				const yDiff = isLastWord || word.classList.contains('v-hp-about__word--long')  ? 60 : bounces[ Math.round((getXFromRect(wordRect) - getXFromRect(prevWord.getBoundingClientRect())) / prevWord.offsetWidth) ];

				bounceTl
				.to(ball, {
					y: () => getYFromRect(wordRect) - yDiff,
					duration: .15,
					repeat: 1,
					yoyo: true,
					ease: 'power1.inOut',
					transformOrigin: 'center bottom',
				}, `word-${i}-start`)
				.to(ball, {
					x: () => getXFromRect(wordRect),
					ease: 'power1.inOut',
					duration: .3,
				}, `word-${i}-start`);
			}

			// squash the ball
			bounceTl
			.addLabel(`word-${i}-squash`)
			.to(ball, {
				scaleY: word === firstWord ? .9 : (word.classList.contains('v-hp-about__word--long') ? .8 : .5),
				duration: .05,
				repeat: 1,
				yoyo: true,
				ease: 'power1.inOut',
				transformOrigin: 'center bottom',
			}, `word-${i}-squash`)
			.to(word, {
				yPercent: 6,
				repeat: 1,
				yoyo: true,
				ease: 'power1.out',
				duration: .1,
				transformOrigin: 'center bottom',
				onComplete: () => {
					gsap.set(word, { clearProps: 'all' });
				},
			}, `word-${i}-squash`);

			// additional tween for last word
			if (isLastWord) {
				bounceTl
				.addLabel('word-last-bounce')
				.to(ball, {
					backgroundColor: dot.dataset.fill,
					duration: 0.2,
					ease: 'power1.inOut',
					transformOrigin: 'center bottom',
				}, 'word-last-bounce')
				.to(ball, {
					y: () => getYFromRect(wordRect) - 60,
					repeat: 1,
					yoyo: true,
					duration: .15,
					ease: 'power1.out',
					transformOrigin: 'center bottom',
				}, 'word-last-bounce+=.3')
				.to(ball, {
					x: () => getXFromRect(wordRect) + 20,
					duration: .3,
					ease: 'power1.out',
					transformOrigin: 'center bottom',
				}, 'word-last-bounce+=.3');
			}
		});

		// Animate to the dot
		bounceTl.to(ball, {
			y: () => ((dotRect.top + window.scrollY) - (sectionRect.top + window.scrollY) + dotRect.height),
			duration: .15,
			ease: 'power1.in',
			transformOrigin: 'center bottom',
		})
		.to(ball, {
			x: () => {
				return (dotRect.left + window.scrollX - dotRect.width) - parseFloat(ball.style.getPropertyValue('--ball-left'), 10);
			},
			duration: .3,
			ease: 'power1.in',
			transformOrigin: 'center bottom',
		}, '<')
		.to(ball, {
			scale: () => {
				return dot.offsetWidth / ball.offsetWidth;
			},
			duration: .3,
			ease: 'power1.inOut',
			transformOrigin: 'center bottom',
		}, '<');

		return bounceTl;
	};

	/**
	 * Rebuild the bounce timeline
	 */
	const rebuildBounceTl = () => {
		if (!bounceTl) {
			return;
		}

		bounceTl.clear(true).invalidate().kill();
		bounceTl = null;
		gsap.set(ball, { clearProps: 'all' });
		dot.classList.remove('visible');
		buildBounceTl();
	};

	// Init
	buildBounceTl();

	// Rebuild on resize
	window.addEventListener('resize', debounce(() => {
		firstLine = getFirstLine();
		dot.classList.remove('visible');
		rebuildBounceTl();
	}, 100));
};

export default vHpAbout;
